import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import InboxIcon from '@mui/icons-material/InboxOutlined';
import { colors, primaryButtonSx, secondaryButtonSx } from '../../theme/tokens';
import WriteButton from './WriteButton';

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * WU12 — optional secondary action (outlined button) rendered alongside
   * the primary CTA. Used by the PromotionPipeline empty state for the
   * "Watch 30s walkthrough" link.
   */
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  /** Use 'search' for filter-empty, 'data' for no-data-yet */
  variant?: 'search' | 'data';
}

export default function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  variant = 'data',
}: EmptyStateProps) {
  const defaultIcon =
    variant === 'search' ? (
      <SearchIcon sx={{ fontSize: 40, color: colors.textTertiary }} />
    ) : (
      <InboxIcon sx={{ fontSize: 40, color: colors.textTertiary }} />
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        px: 3,
        textAlign: 'center',
      }}
    >
      {/* Circular background behind icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: '#F3F4F6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
        }}
      >
        {icon || defaultIcon}
      </Box>
      <Typography
        sx={{
          fontSize: 17,
          fontWeight: 700,
          fontFamily: 'Manrope, sans-serif',
          color: colors.textPrimary,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{
            fontSize: 14,
            color: colors.textSecondary,
            maxWidth: 380,
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      )}
      {(actionLabel || secondaryActionLabel) && (
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          {actionLabel && onAction && (
            <WriteButton
              variant="contained"
              size="small"
              onClick={onAction}
              sx={primaryButtonSx}
            >
              {actionLabel}
            </WriteButton>
          )}
          {secondaryActionLabel && onSecondaryAction && (
            <WriteButton
              variant="outlined"
              size="small"
              onClick={onSecondaryAction}
              sx={secondaryButtonSx}
            >
              {secondaryActionLabel}
            </WriteButton>
          )}
        </Box>
      )}
    </Box>
  );
}
